import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Segment } from 'semantic-ui-react';
import { uploadData } from 'aws-amplify/storage';
import { getCurrentUser } from 'aws-amplify/auth';
import './Shapes.css';

const PVT = () => {
  // --- State ---
  const [counter, setCounter] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [Ntrials, setNtrials] = useState(0);
  const [lastCounter, setLastCounter] = useState(null);
  const [currentDelay, setDelay] = useState(null);
  const [stimStartTime, setStimStartTime] = useState(null);
  const [counterHistory, setCounterHistory] = useState([]);
  const [uploadStatus, setUploadStatus] = useState('');

  const [correctiveVision, setCorrectiveVision] = useState('');
  const [inputDevice, setInputDevice] = useState('');
  const [ambientLight, setAmbientLight] = useState('');
  const [distractionLevel, setDistractionLevel] = useState('');
  const [kssSleepiness, setKssSleepiness] = useState('');

  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [isAnimationStarted, setIsAnimationStarted] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // --- Refs ---
  const startTimeRef = useRef(null);
  const startTimeRefAll = useRef(null);
  const timerRef = useRef(null);

  // --- Device Info ---
  const isMobileDevice = () =>
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  const getDeviceCondition = () => {
    const ua = navigator.userAgent || '';
    const isTouch = 'ontouchstart' in window;
    if (/iPhone|iPad|iPod|Android/i.test(ua)) return 1;
    if (!isTouch) return 2;
    return 3;
  };

  const getDeviceInfo = useCallback(() => ({
    deviceConditionId: getDeviceCondition(),
    isMobile,
    inputDevice: isMobile ? 'touch' : inputDevice,
    correctiveVision,
    ambientLight,
    distractionLevel,
    kssSleepiness,
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    devicePixelRatio: window.devicePixelRatio,
    orientation: window.screen.orientation?.type || 'unknown',
    visibilityState: document.visibilityState,
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    totalTrials: Ntrials,
  }), [isMobile, inputDevice, correctiveVision, ambientLight, distractionLevel, kssSleepiness, Ntrials]);

  useEffect(() => {
    setIsMobile(isMobileDevice());
    console.log('Device info:', getDeviceInfo());
  }, [getDeviceInfo]);

  // --- Resize handling ---
  useEffect(() => {
    const container = document.getElementById('container');
    if (container) setContainerSize({ width: container.offsetWidth / 2, height: container.offsetHeight / 2 });
    const handleResize = () => {
      if (container) setContainerSize({ width: container.offsetWidth / 2, height: container.offsetHeight / 2 });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // --- Counter updates ---
  useEffect(() => {
    if (!isAnimationStarted) return;
    const intervalId = setInterval(() => setCounter(Math.round(performance.now() - startTimeRef.current)), 1);
    return () => clearInterval(intervalId);
  }, [isAnimationStarted]);

  // --- Elapsed session time ---
  useEffect(() => {
    if (Ntrials === 0) return;
    const elapsed = Math.round(((Date.now() - startTimeRefAll.current) / 1000 / 60) * 100) / 100;
    setElapsedTime(elapsed);
  }, [Ntrials]);

  // --- Animation control ---
  const startAnimation = () => {
    setNtrials(prev => prev + 1);
    if (Ntrials === 0) startTimeRefAll.current = Date.now();
    if (timerRef.current) clearTimeout(timerRef.current);

    const delay = Math.random() * 4000 + 1000;
    setDelay(delay);

    timerRef.current = setTimeout(() => {
      setIsAnimationStarted(true);
      startTimeRef.current = performance.now();
      setStimStartTime(new Date().toISOString());
    }, delay);
  };

  const reportTrial = () => {
    setIsAnimationStarted(false);
    setLastCounter(counter);
    setCounterHistory([...counterHistory, { delay: currentDelay, counter, stimStartTime }]);
    setCounter(0);
    startAnimation();
  };

  // --- Upload ---
  const uploadCSV = async () => {
    const metaRows = Object.entries(getDeviceInfo()).map(([k, v]) => `${k}: ${v}`);
    const header = 'ISI(ms),RT(ms),StimStartTime(UTC)';
    const dataRows = counterHistory.map(e => `${e.delay},${e.counter},${e.stimStartTime}`);
    const csvContent = `${metaRows.join('\n')}\n--\n${header}\n${dataRows.join('\n')}`;

    try {
      setUploadStatus('Uploading...');
      const user = await getCurrentUser();
      const username = user.username;
      const epochTime = Math.floor(Date.now() / 1000);
      const currentTime = new Date();
      const offsetMinutes = currentTime.getTimezoneOffset();
      const offsetHours = Math.abs(Math.floor(offsetMinutes / 60));
      const offsetMinutesRemaining = Math.abs(offsetMinutes % 60);
      const offsetSign = offsetMinutes < 0 ? '+' : '-';
      const timezone = `UTC${offsetSign}${offsetHours.toString().padStart(2,'0')}:${offsetMinutesRemaining.toString().padStart(2,'0')}`;
      const filename = `pvt_${epochTime}_${timezone}.csv`;
      const folderPath = `${username}/pvt/`;

      await uploadData({
        key: folderPath + filename,
        data: csvContent,
        options: { contentType: 'text/csv', accessLevel: 'private' },
      }).result;

      setUploadStatus('✅ File uploaded successfully');
    } catch (error) {
      console.error(error);
      setUploadStatus(`❌ Error: ${error.message}`);
    }
  };

  const resetAll = () => {
    setCounter(0); setElapsedTime(0); setNtrials(0); setLastCounter(null);
    setCounterHistory([]); setUploadStatus('');
    setCorrectiveVision(''); setInputDevice('');
    setAmbientLight(''); setDistractionLevel(''); setKssSleepiness('');
  };

  // --- KSS Options ---
  const KSS_OPTIONS = [
    { value: 1, label: '1 - Extremely alert' },
    { value: 2, label: '2 - Very alert' },
    { value: 3, label: '3 - Alert' },
    { value: 4, label: '4 - Rather alert' },
    { value: 5, label: '5 - Neither alert nor sleepy' },
    { value: 6, label: '6 - Some signs of sleepiness' },
    { value: 7, label: '7 - Sleepy, but no effort to keep awake' },
    { value: 8, label: '8 - Sleepy, some effort to keep awake' },
    { value: 9, label: '9 - Very sleepy, great effort to keep awake, fighting sleep' }
  ];

  // --- Render ---
  return (
    <div id="container" style={{ display:'flex', justifyContent:'center', alignItems:'center', marginTop:'15em', height:'90vh', width:'90vw', flexDirection:'column' }}>
      <Segment basic style={{ textAlign:'center', width:'100%' }}>

        {/* === Metadata Inputs at TOP === */}
        <div style={{ marginBottom:'1em' }}>
          <label>Corrective vision:
            <select value={correctiveVision} onChange={e=>setCorrectiveVision(e.target.value)} style={{ marginLeft:'0.5em' }}>
              <option value="">Select</option>
              <option value="naked">Naked</option>
              <option value="glasses">Glasses</option>
              <option value="contacts">Contacts</option>
              <option value="reading_glasses">Reading Glasses</option>
            </select>
          </label>
        </div>

        {!isMobile && (
          <div style={{ marginBottom:'1em' }}>
            <p>Select input device:</p>
            <label><input type="radio" name="inputDevice" value="keyboard" checked={inputDevice==='keyboard'} onChange={e=>setInputDevice(e.target.value)} /> Keyboard</label>
            <label style={{ marginLeft:'1em' }}><input type="radio" name="inputDevice" value="mouse" checked={inputDevice==='mouse'} onChange={e=>setInputDevice(e.target.value)} /> Mouse</label>
          </div>
        )}

        <div style={{ marginBottom:'1em' }}>
          <label>Ambient light (lux):
            <input type="number" value={ambientLight} onChange={e=>setAmbientLight(e.target.value)} style={{ marginLeft:'0.5em' }}/>
          </label>
        </div>

        <div style={{ marginBottom:'1em' }}>
          <label>External Distraction Level (1–5):
            <select value={distractionLevel} onChange={e=>setDistractionLevel(e.target.value)} style={{ marginLeft:'0.5em' }}>
              <option value="">Select</option>
              <option value="1">1 - Minimal</option>
              <option value="2">2</option>
              <option value="3">3</option>
              <option value="4">4</option>
              <option value="5">5 - Very Distracted</option>
            </select>
          </label>
        </div>

        <div style={{ marginBottom:'2em' }}>
          <label>Sleepiness (KSS):
            <select value={kssSleepiness} onChange={e => setKssSleepiness(e.target.value)} style={{ marginLeft:'0.5em' }}>
              <option value="">Select</option>
              {KSS_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </label>
        </div>

        {/* === Timer Box === */}
        <h1>Timer</h1>
        <h3>(click box to start)</h3>
        <div
          className="rectangle"
          onClick={() => reportTrial()}
          style={{
            touchAction:'manipulation',
            width:containerSize.width,
            height:containerSize.height,
            display:'flex',
            justifyContent:'center',
            alignItems:'center',
            textAlign:'left',
            margin:'0 auto',
            fontSize:'20px'
          }}
        >
          {`${elapsedTime} (mins) elapsed session time`}<br/>
          {`Counter: ${counter}`}<br/>
          {`Last RT (ms): ${lastCounter}`}<br/>
        </div>

        {/* === Bottom Buttons === */}
        <div style={{ display:'flex', justifyContent:'center', marginTop:'2em' }}>
          <button style={{ marginRight:'1em' }} onClick={() => { setIsAnimationStarted(false); setCounter(0); }}>Stop</button>
          <button style={{ marginRight:'1em' }} onClick={uploadCSV}>Upload</button>
          <button onClick={resetAll}>Reset</button>
        </div>

        <div className="upload-status" style={{ marginTop:'1em' }}>{uploadStatus}</div>
      </Segment>
    </div>
  );
};

export default PVT;
